import { z } from 'zod';

export const RecommendedActionSchema = z.object({
    actionType: z
        .enum([
            'SEND_EMAIL',
            'SEND_LINKEDIN_MESSAGE',
            'PHONE_CALL',
            'MEETING',
            'MAKE_INTRODUCTION',
            'SCHEDULE_CALL',
            'SCHEDULE_MEETING',
            'SEND_FOLLOW_UP',
            'SHARE_CONTENT',
            'ESCALATE_INTERNALLY',
            'RESEARCH_MORE',
            'NURTURE_RELATIONSHIP',
            'WAIT_FOR_RESPONSE',
            'NONE',
        ])
        .describe(
            'The type of action to take, PAUSE means no action should be taken. If the recommended action is some form of outreach, then the actionType should use the same as what used for the previous messages',
        ),
    priorityScore: z.number().int().min(0).max(100).describe('The priority score of the action'),
    reason: z
        .string()
        .max(300)
        .describe(
            "The reason for recommending this action, and why it matters to the user. Do not mention any specific metadata attributes as the reason (avoid phrases such as 'scheduled follow up after X days' or 'positive sentiment' - rather 'follow up to make sure they read your last message about' or 'they seem interested in your proposal'). Put it in terms they will quickly understand with only the user's own understanding of the contact and their relationship.",
        ),
    nextSuggestedUserActionInDaysFromLastMessage: z
        .number()
        .nullable()
        .describe(
            'Number of days after the last message before the user is suggested or expected to take the next action (reply, follow up, etc.) in the conversation. This is the date the action will be made visible to the user. Null if not applicable.',
        ),
});

export const ActionMetadataSchema = z
    .object({
        recommendedActions: z
            .array(RecommendedActionSchema)
            .max(10)
            .describe('All recommendations of possible relevant actions to take'),
        topActionType: RecommendedActionSchema.shape.actionType.describe('The recommended top action to take'),
        priorityScore: z.number().int().min(0).max(100).describe('The priority score of the top action'),
        nextSuggestedUserActionInDaysFromLastMessage: z
            .number()
            .nullable()
            .describe('The nextSuggestedUserActionInDaysFromLastMessage from the top action'),
    })
    .passthrough();

export type ActionMetadata = z.infer<typeof ActionMetadataSchema>;
export type ActionType = z.infer<typeof ActionMetadataSchema.shape.topActionType>;
